(function () {
  // Enforces the gift-option rules that All in gift cannot express on its own.
  // The app renders every option as a plain radio list and always leaves the
  // free-text field editable, so the storefront has to apply these itself:
  //
  //   1. A noshi marked 名入不可 (and 「なし」) accepts no inscription.
  //   2. Only 「オリジナルメッセージ」 accepts a message body.
  //   3. That message body is capped at 60 characters.
  //
  // The modal is a React/Chakra tree that re-renders on every interaction, so
  // the rules are re-applied from a MutationObserver rather than bound once.
  const MODAL_SELECTOR = 'chakra-scope .chakra-modal__content';
  const NOSHI_TITLE = 'のし紙';
  const MESSAGE_CARD_TITLE = 'メッセージカード';
  const NO_INSCRIPTION_MARK = '名入不可';
  const NO_NOSHI_LABEL = /のし\s*-\s*なし(\s|$)/;
  const FREE_TEXT_CARD = 'オリジナルメッセージ';
  const MESSAGE_MAX_LENGTH = 60;
  const NOTE_ATTRIBUTE = 'data-aig-note';
  const WIDGET_SELECTOR = 'chakra-scope';
  const NEXT_STEP_LABEL = '次の設定へ';

  // 「のしのみ選択可」タグが付いた商品ではメッセージカードを選ばせない。
  // フラグは snippets/all-in-gift-card-exclusion.liquid が商品タグを見て立てる。
  const HIDE_MESSAGE_CARD = window.allInGiftHideMessageCard === true;
  // 「カードのみ選択可」タグが付いた商品ではのし紙を選ばせない。
  const HIDE_NOSHI = window.allInGiftHideNoshi === true;

  const NOTES = {
    [NOSHI_TITLE]: '※選択されたのしは名入れに対応していません。',
    [MESSAGE_CARD_TITLE]:
      '※「' + FREE_TEXT_CARD + '」を選択された場合のみ、本文をご記入いただけます。',
  };

  // React owns the field value, so assigning `.value` alone is discarded on the
  // next render. Write through the native setter and replay the input event.
  function clearField(field) {
    if (field.value === '') return;

    const setter = Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype,
      'value'
    ).set;

    setter.call(field, '');
    field.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function modalTitle(modal) {
    return (modal.innerText || '').trim().split('\n')[0].trim();
  }

  function selectedOptionLabel(modal) {
    const checked = modal.querySelector('input[type="radio"]:checked');
    return checked ? checked.closest('label')?.textContent || '' : '';
  }

  function noteFor(field, title) {
    let note = field.parentElement?.querySelector('[' + NOTE_ATTRIBUTE + ']');

    if (!note) {
      note = document.createElement('p');
      note.setAttribute(NOTE_ATTRIBUTE, '');
      note.className = 'all-in-gift-option-note';
      field.insertAdjacentElement('afterend', note);
    }

    note.textContent = NOTES[title] || '';
    return note;
  }

  function setFieldEnabled(field, enabled, title) {
    if (field.disabled === !enabled) {
      // Already in the right state; only the note may still need showing.
      noteFor(field, title).hidden = enabled;
      return;
    }

    field.disabled = !enabled;
    noteFor(field, title).hidden = enabled;

    if (!enabled) clearField(field);
  }

  // 閉じた直後のモーダルは aria-hidden を付けたまま DOM に残る。
  // 先頭の1件だけを見ると、その残骸を今開いているモーダルと取り違える。
  function activeModals() {
    const modals = document.querySelectorAll(MODAL_SELECTOR);

    return [...modals].filter(function (modal) {
      return !modal.closest('[aria-hidden="true"]');
    });
  }

  function applyRules() {
    for (const modal of activeModals()) applyModalRules(modal);
  }

  function applyModalRules(modal) {
    const field = modal.querySelector('textarea');
    if (!field) return;

    const title = modalTitle(modal);
    const label = selectedOptionLabel(modal);

    if (title === NOSHI_TITLE) {
      const inscribable =
        label !== '' &&
        !label.includes(NO_INSCRIPTION_MARK) &&
        !NO_NOSHI_LABEL.test(label);

      setFieldEnabled(field, inscribable, title);
      return;
    }

    if (title === MESSAGE_CARD_TITLE) {
      setFieldEnabled(field, label.includes(FREE_TEXT_CARD), title);

      field.maxLength = MESSAGE_MAX_LENGTH;
      if (field.value.length > MESSAGE_MAX_LENGTH) {
        const setter = Object.getOwnPropertyDescriptor(
          HTMLTextAreaElement.prototype,
          'value'
        ).set;
        setter.call(field, field.value.slice(0, MESSAGE_MAX_LENGTH));
        field.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  // chakra 側のスタイルに負けないよう !important で当てる。
  function hide(element) {
    if (element) element.style.setProperty('display', 'none', 'important');
  }

  // 対象オプションへの導線を塞ぐ。React が作り直すたびに当て直すため、
  // applyRules と同じ監視ループから呼ぶ。
  function hideGiftOptionEntries() {
    if (!HIDE_MESSAGE_CARD && !HIDE_NOSHI) return;

    // ギフトオプション一覧の対象行。
    // 複数配送では贈る相手ごとに増えるので全件処理する。
    const labels = document.querySelectorAll(WIDGET_SELECTOR + ' p');

    for (const label of labels) {
      const title = label.textContent.trim();
      const shouldHide =
        (HIDE_MESSAGE_CARD && title === MESSAGE_CARD_TITLE) ||
        (HIDE_NOSHI && title === NOSHI_TITLE);
      if (!shouldHide) continue;
      if (label.closest('.chakra-modal__content')) continue; // モーダルの見出しは行ではない
      hide(label.closest('.chakra-stack'));
    }

    // のしのみ選択可では、のし紙モーダルの「次の設定へ」も隠す。有効なギフトオプションが のし紙 と
    // メッセージカードの2つだけなので、ここが唯一のカード選択モーダルへの導線になる。
    if (HIDE_MESSAGE_CARD) {
      for (const modal of activeModals()) {
        if (modalTitle(modal) !== NOSHI_TITLE) continue;

        for (const button of modal.querySelectorAll('button')) {
          if (button.textContent.trim() === NEXT_STEP_LABEL) hide(button);
        }
      }
    }
  }

  let queued = false;

  // Deliberately a timer rather than requestAnimationFrame: rAF is paused in
  // background tabs, so the rules would not apply until the tab was focused.
  function scheduleApply() {
    if (queued) return;

    queued = true;
    setTimeout(function () {
      queued = false;
      applyRules();
      hideGiftOptionEntries();
    }, 0);
  }

  new MutationObserver(scheduleApply).observe(document.body, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('change', scheduleApply);
  scheduleApply();
})();
